/**
 * WaveformClip Component
 * Represents an audio clip with waveform visualization
 * Includes trim handles, fade handles, and selection state
 */
import { useRef, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { WaveformCanvas } from './WaveformCanvas';
import { FadeHandle } from './FadeHandle';
import { TrimHandle } from './TrimHandle';
import { LoopMarker } from './LoopMarker';

export const WaveformClip = ({
  clip,
  track,
  audioBuffer,
  waveformData = [],
  isSelected = false,
  isPlaying = false,
  currentTime = 0,
  totalDuration = 60,
  zoom = 1,
  onSelect,
  onFadeChange,
  onLoopChange,
  onTrimChange,
  onSeek,
  onSelectionChange,
  selection = null,
  snapEnabled = true,
  gridSize = 0.5,
  bpm = 120,
  className = '',
}) => {
  const clipRef = useRef(null);
  const containerRef = useRef(null);
  const [isDraggingFadeIn, setIsDraggingFadeIn] = useState(false);
  const [isDraggingFadeOut, setIsDraggingFadeOut] = useState(false);
  const [isDraggingTrimStart, setIsDraggingTrimStart] = useState(false);
  const [isDraggingTrimEnd, setIsDraggingTrimEnd] = useState(false);
  const [localFadeIn, setLocalFadeIn] = useState(clip?.fadeIn || 0);
  const [localFadeOut, setLocalFadeOut] = useState(clip?.fadeOut || 0);
  
  // Trim state - represents how much audio is trimmed from start/end
  // Initialize from props and let them be controlled locally during drag
  const [localTrimStart, setLocalTrimStart] = useState(clip?.trimStart || 0);
  const [localTrimEnd, setLocalTrimEnd] = useState(clip?.trimEnd || 0);
  
  // Use refs to track if values were changed externally (e.g., undo/redo)
  const prevTrimStartRef = useRef(clip?.trimStart);
  const prevTrimEndRef = useRef(clip?.trimEnd);
  
  // Sync local state with props only when props change from external source
  if (clip?.trimStart !== prevTrimStartRef.current) {
    prevTrimStartRef.current = clip?.trimStart;
    if (clip?.trimStart !== localTrimStart) {
      setLocalTrimStart(clip?.trimStart || 0);
    }
  }
  if (clip?.trimEnd !== prevTrimEndRef.current) {
    prevTrimEndRef.current = clip?.trimEnd;
    if (clip?.trimEnd !== localTrimEnd) {
      setLocalTrimEnd(clip?.trimEnd || 0);
    }
  }
  
  // Original audio duration (before any trimming)
  const originalDuration = audioBuffer?.duration || clip?.originalDuration || clip?.duration || track?.duration || 0;
  
  // Actual clip timing after trimming
  const clipStartTime = clip?.startTime ?? track?.startTime ?? 0;
  const clipDuration = originalDuration - localTrimStart - localTrimEnd;
  
  // Calculate clip position as percentage
  const clipLeftPercent = (clipStartTime / totalDuration) * 100 * zoom;
  const clipWidthPercent = Math.max(0.5, (clipDuration / totalDuration) * 100 * zoom);
  
  // Handle fade in drag
  const handleFadeInDrag = useCallback((e, deltaX) => {
    if (!clipRef.current) return;
    
    const clipWidth = clipRef.current.offsetWidth;
    const deltaTime = (deltaX / clipWidth) * clipDuration;
    const newFadeIn = Math.max(0, Math.min(localFadeIn + deltaTime, clipDuration - localFadeOut));
    
    setLocalFadeIn(newFadeIn);
  }, [clipDuration, localFadeIn, localFadeOut]);

  const handleFadeInDragEnd = useCallback(() => {
    setIsDraggingFadeIn(false);
    onFadeChange?.({ fadeIn: localFadeIn, fadeOut: localFadeOut });
  }, [localFadeIn, localFadeOut, onFadeChange]);

  // Handle fade out drag
  const handleFadeOutDrag = useCallback((e, deltaX) => {
    if (!clipRef.current) return;
    
    const clipWidth = clipRef.current.offsetWidth;
    const deltaTime = -(deltaX / clipWidth) * clipDuration;
    const newFadeOut = Math.max(0, Math.min(localFadeOut + deltaTime, clipDuration - localFadeIn));
    
    setLocalFadeOut(newFadeOut);
  }, [clipDuration, localFadeIn, localFadeOut]);

  const handleFadeOutDragEnd = useCallback(() => {
    setIsDraggingFadeOut(false);
    onFadeChange?.({ fadeIn: localFadeIn, fadeOut: localFadeOut });
  }, [localFadeIn, localFadeOut, onFadeChange]);

  // Handle trim start drag
  const handleTrimStartDrag = useCallback((e, deltaX) => {
    if (!containerRef.current) return;
    
    // Get the timeline container width to calculate time delta
    const timeline = containerRef.current.closest('[data-testid^="track-lane"]');
    if (!timeline) return;
    
    const timelineWidth = timeline.offsetWidth;
    const deltaTime = (deltaX / timelineWidth) * totalDuration / zoom;
    
    // Calculate new trim start (clamped to valid range)
    const maxTrimStart = originalDuration - localTrimEnd - 0.1; // Minimum 0.1s clip
    const newTrimStart = Math.max(0, Math.min(localTrimStart + deltaTime, maxTrimStart));
    
    setLocalTrimStart(newTrimStart);
  }, [originalDuration, localTrimStart, localTrimEnd, totalDuration, zoom]);

  const handleTrimStartDragEnd = useCallback(() => {
    setIsDraggingTrimStart(false);
    onTrimChange?.({ 
      trimStart: localTrimStart, 
      trimEnd: localTrimEnd,
      newStartTime: clipStartTime + localTrimStart,
      newDuration: originalDuration - localTrimStart - localTrimEnd
    });
  }, [localTrimStart, localTrimEnd, clipStartTime, originalDuration, onTrimChange]);

  // Handle trim end drag
  const handleTrimEndDrag = useCallback((e, deltaX) => {
    if (!containerRef.current) return;
    
    // Get the timeline container width to calculate time delta
    const timeline = containerRef.current.closest('[data-testid^="track-lane"]');
    if (!timeline) return;
    
    const timelineWidth = timeline.offsetWidth;
    const deltaTime = -(deltaX / timelineWidth) * totalDuration / zoom;
    
    // Calculate new trim end (clamped to valid range)
    const maxTrimEnd = originalDuration - localTrimStart - 0.1; // Minimum 0.1s clip
    const newTrimEnd = Math.max(0, Math.min(localTrimEnd + deltaTime, maxTrimEnd));
    
    setLocalTrimEnd(newTrimEnd);
  }, [originalDuration, localTrimStart, localTrimEnd, totalDuration, zoom]);

  const handleTrimEndDragEnd = useCallback(() => {
    setIsDraggingTrimEnd(false);
    onTrimChange?.({ 
      trimStart: localTrimStart, 
      trimEnd: localTrimEnd,
      newDuration: originalDuration - localTrimStart - localTrimEnd
    });
  }, [localTrimStart, localTrimEnd, originalDuration, onTrimChange]);

  // Handle click to select
  const handleClick = useCallback((e) => {
    e.stopPropagation();
    onSelect?.();
  }, [onSelect]);

  // Calculate fade handle positions
  const fadeInPercent = clipDuration > 0 ? (localFadeIn / clipDuration) * 100 : 0;
  const fadeOutPercent = clipDuration > 0 ? 100 - (localFadeOut / clipDuration) * 100 : 100;

  return (
    <motion.div
      ref={(el) => { clipRef.current = el; containerRef.current = el; }}
      className={`
        absolute top-0 bottom-0 
        rounded-md overflow-visible 
        border transition-colors
        ${isSelected 
          ? 'ring-2 ring-indigo-500 border-indigo-400 bg-indigo-500/20' 
          : 'border-indigo-500/30 bg-indigo-500/10 hover:border-indigo-400'
        }
        ${isDraggingTrimStart || isDraggingTrimEnd ? 'ring-2 ring-cyan-500' : ''}
        ${className}
      `}
      style={{
        left: `${clipLeftPercent}%`,
        width: `${clipWidthPercent}%`,
        minWidth: '60px',
      }}
      onClick={handleClick}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      data-testid={`waveform-clip-${track?.id || 'unknown'}`}
    >
      {/* Trim handles - only show when selected */}
      {isSelected && (
        <>
          {/* Trim Start Handle (left side) */}
          <TrimHandle
            type="start"
            onDrag={handleTrimStartDrag}
            onDragStart={() => setIsDraggingTrimStart(true)}
            onDragEnd={handleTrimStartDragEnd}
            isDragging={isDraggingTrimStart}
            trimAmount={localTrimStart}
          />
          
          {/* Trim End Handle (right side) */}
          <TrimHandle
            type="end"
            onDrag={handleTrimEndDrag}
            onDragStart={() => setIsDraggingTrimEnd(true)}
            onDragEnd={handleTrimEndDragEnd}
            isDragging={isDraggingTrimEnd}
            trimAmount={localTrimEnd}
          />
        </>
      )}

      {/* Waveform visualization */}
      <div className="absolute inset-0 overflow-hidden rounded-md">
        <WaveformCanvas
          audioBuffer={audioBuffer}
          waveformData={waveformData}
          duration={clipDuration}
          currentTime={currentTime - clipStartTime}
          fadeIn={localFadeIn}
          fadeOut={localFadeOut}
          loopStart={clip?.loopStart}
          loopEnd={clip?.loopEnd}
          isPlaying={isPlaying}
          onSeek={(time) => onSeek?.(clipStartTime + time)}
          onSelectionChange={onSelectionChange}
          selection={selection}
          trimStart={localTrimStart}
          className="h-full"
        />
      </div>

      {/* Fade handles - only show when selected */}
      {isSelected && (
        <>
          {/* Fade In Handle */}
          <FadeHandle
            type="fadeIn"
            position={fadeInPercent}
            onDrag={handleFadeInDrag}
            onDragStart={() => setIsDraggingFadeIn(true)}
            onDragEnd={handleFadeInDragEnd}
            isDragging={isDraggingFadeIn}
          />
          
          {/* Fade Out Handle */}
          <FadeHandle
            type="fadeOut"
            position={fadeOutPercent}
            onDrag={handleFadeOutDrag}
            onDragStart={() => setIsDraggingFadeOut(true)}
            onDragEnd={handleFadeOutDragEnd}
            isDragging={isDraggingFadeOut}
          />
        </>
      )}

      {/* Loop markers - only show when selected and loop is set */}
      {isSelected && clip?.loopStart !== null && clip?.loopEnd !== null && (
        <>
          <LoopMarker
            type="start"
            position={(clip.loopStart / clipDuration) * 100}
            onDrag={(newPos) => {
              const newTime = (newPos / 100) * clipDuration;
              onLoopChange?.({ loopStart: newTime, loopEnd: clip.loopEnd });
            }}
          />
          <LoopMarker
            type="end"
            position={(clip.loopEnd / clipDuration) * 100}
            onDrag={(newPos) => {
              const newTime = (newPos / 100) * clipDuration;
              onLoopChange?.({ loopStart: clip.loopStart, loopEnd: newTime });
            }}
          />
        </>
      )}

      {/* Clip name label */}
      <div className="absolute bottom-1 left-4 right-4 flex items-center justify-between pointer-events-none">
        <span className="text-[10px] font-medium text-white/70 truncate">
          {clip?.name || track?.name || 'Audio'}
        </span>
        {clipDuration > 0 && (
          <span className="text-[9px] font-mono text-white/50">
            {Math.floor(clipDuration / 60)}:{String(Math.floor(clipDuration % 60)).padStart(2, '0')}
          </span>
        )}
      </div>

      {/* Fade in/out visual indicators */}
      {localFadeIn > 0 && (
        <div 
          className="absolute top-0 left-0 h-full bg-gradient-to-r from-black/60 to-transparent pointer-events-none rounded-l-md"
          style={{ width: `${fadeInPercent}%` }}
        />
      )}
      {localFadeOut > 0 && (
        <div 
          className="absolute top-0 right-0 h-full bg-gradient-to-l from-black/60 to-transparent pointer-events-none rounded-r-md"
          style={{ width: `${100 - fadeOutPercent}%` }}
        />
      )}
      
      {/* Trim indicators (subtle visual hint of trimmed portions) */}
      {(localTrimStart > 0 || localTrimEnd > 0) && isSelected && (
        <div className="absolute -top-1 left-1/2 -translate-x-1/2 px-1.5 py-0.5 bg-cyan-500/20 rounded text-[8px] font-mono text-cyan-300 pointer-events-none">
          {localTrimStart > 0 && <span>◀{localTrimStart.toFixed(1)}s</span>}
          {localTrimStart > 0 && localTrimEnd > 0 && <span className="mx-1">|</span>}
          {localTrimEnd > 0 && <span>{localTrimEnd.toFixed(1)}s▶</span>}
        </div>
      )}
    </motion.div>
  );
};

export default WaveformClip;
